import type { CategoryStanding, CourseStanding, GradedItem, ToGrade } from '@fall26/shared'
import { useState } from 'react'
import { useCourses, useGrades, useSetCategoryScore, useSetScore } from '../api/hooks.js'
import { CourseDot } from '../components/CourseDot.js'
import { ScoreBox } from '../components/grades/ScoreBox.js'
import { fmt } from '../components/grades/score-text.js'

/**
 * Grades.
 *
 * Every number here was computed by the server. The drop rules are real and
 * specific — ME 330 drops two quizzes, ME 134L drops two pre-lab quizzes — and
 * doing that arithmetic twice, once here and once there, is how the two
 * answers quietly diverge. The client renders what it was handed and computes
 * nothing.
 *
 * It is also where grades get ENTERED. The old path was the edit sheet, three
 * taps deep in Work; it produced one score in three weeks against a backlog of
 * 26. Now the backlog is the first thing on this screen, and every score box
 * is on the row it belongs to.
 */
export function Grades() {
  const grades = useGrades()
  const courses = useCourses()

  if (grades.isError) {
    return (
      <p className="rounded-[var(--radius)] border border-[var(--overdue)]/30 bg-[var(--overdue-wash)] p-4 text-sm text-[var(--overdue)]">
        Could not load your grades.
      </p>
    )
  }
  if (!grades.data) return <div className="h-64 animate-pulse rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]" />

  const hue = new Map<string, number>((courses.data?.courses ?? []).map((c) => [String(c.id), c.colorIndex]))

  return (
    <section className="space-y-4">
      <h1 className="text-lg font-semibold tracking-tight text-[var(--text)]">Grades</h1>

      <ToGradeList rows={grades.data.toGrade} hue={hue} />

      <div className="space-y-3">
        {grades.data.courses.map((course) => (
          <CourseCard key={course.courseId} course={course} colorIndex={hue.get(course.courseId) ?? 0} />
        ))}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// the backlog
// ---------------------------------------------------------------------------

function ToGradeList({ rows, hue }: { rows: ToGrade[]; hue: Map<string, number> }) {
  const setScore = useSetScore()
  if (rows.length === 0) return null

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--soon)]/35 bg-[var(--surface)] p-3.5">
      <div className="flex items-baseline gap-2">
        <h2 className="tabular text-[11px] uppercase tracking-[0.14em] text-[var(--soon)]">To grade</h2>
        <span className="tabular text-[11px] text-[var(--text-subtle)]">{rows.length}</span>
        <span className="text-[11px] text-[var(--text-subtle)]">— past due, no score yet</span>
      </div>
      <ul className="mt-2 divide-y divide-[var(--rule)]">
        {rows.map((row) => (
          <li key={row.item.itemId} className="space-y-1.5 py-2.5">
            <div className="flex items-center gap-1.5">
              <CourseDot colorIndex={hue.get(row.courseId) ?? 0} />
              <span className="tabular truncate text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
                {row.courseCode} · {row.categoryLabel}
              </span>
              <span className="tabular ml-auto shrink-0 text-[10px] text-[var(--text-subtle)]">{row.item.dueDisplay}</span>
            </div>
            <p className="text-[13px] leading-snug text-[var(--text)]">{row.item.title}</p>
            <ScoreBox
              current={null}
              maxPoints={row.item.maxPoints}
              busy={setScore.isPending}
              onSave={(body) => setScore.mutate({ id: row.item.itemId, body, title: row.item.title })}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// per course
// ---------------------------------------------------------------------------

function CourseCard({ course, colorIndex }: { course: CourseStanding; colorIndex: number }) {
  const [open, setOpen] = useState(false)

  return (
    <article className="relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]">
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: `var(--course-${colorIndex % 6})` }}
      />

      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="tap flex w-full items-center gap-3 p-3.5 pl-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <CourseDot colorIndex={colorIndex} />
            <span className="tabular text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
              {course.code}
            </span>
          </div>
          <h2 className="mt-0.5 truncate text-[14px] text-[var(--text)]">{course.name}</h2>
        </div>

        <div className="shrink-0 text-right">
          {course.standingPct === null ? (
            <span className="tabular text-[12px] text-[var(--text-subtle)]">no scores yet</span>
          ) : (
            <>
              <span className="tabular text-[20px] font-semibold text-[var(--text)]">
                {course.standingPct.toFixed(1)}
                <span className="text-[13px] font-normal text-[var(--text-subtle)]">%</span>
              </span>
              <span className="tabular block text-[10px] text-[var(--text-subtle)]">
                of {course.gradedWeightPct}% graded
              </span>
            </>
          )}
        </div>
        <span aria-hidden className="tabular text-[11px] text-[var(--text-subtle)]">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div className="space-y-2 border-t border-[var(--rule)] px-4 pt-3 pb-4">
          {course.categories.map((cat) => (
            <Category key={cat.key} courseId={course.courseId} cat={cat} />
          ))}

          {course.neededOnFinal.length > 0 && (
            <div className="pt-1">
              <div className="tabular mb-1 text-[10px] uppercase tracking-[0.12em] text-[var(--text-subtle)]">
                What the final needs
              </div>
              <div className="flex flex-wrap gap-1.5">
                {course.neededOnFinal.map((n) => (
                  <span
                    key={n.targetLabel}
                    className={`tabular rounded-[var(--radius-sm)] border px-2 py-1 text-[11px] ${
                      n.alreadySecured
                        ? 'border-[var(--done)]/35 bg-[var(--done-wash)] text-[var(--done)]'
                        : n.achievable
                          ? 'border-[var(--border)] text-[var(--text)]'
                          : 'border-[var(--overdue)]/30 bg-[var(--overdue-wash)] text-[var(--overdue)]'
                    }`}
                  >
                    <span className="font-semibold">{n.targetLabel}</span>{' '}
                    {n.alreadySecured
                      ? 'secured'
                      : n.achievable
                        ? `needs ${n.neededPct.toFixed(1)}%`
                        : 'out of reach'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {course.watch && (
            <p className="rounded-[var(--radius)] bg-[var(--surface-sunken)] px-2.5 py-2 text-[12px] leading-snug text-[var(--text-muted)]">
              {course.watch}
            </p>
          )}
        </div>
      )}
    </article>
  )
}

/**
 * One category: its rule, its average, and -- opened -- every item with its
 * score in a box. A wrong entry is fixed on the row it is wrong on, not hunted
 * for in Work.
 */
function Category({ courseId, cat }: { courseId: string; cat: CategoryStanding }) {
  const [open, setOpen] = useState(false)
  const rule = cat.keepHighestN !== null ? `best ${cat.keepHighestN}` : cat.dropLowest > 0 ? `drop ${cat.dropLowest}` : null
  const itemless = cat.items.length === 0

  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="tap flex w-full items-center gap-2 px-2.5 text-left"
      >
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--text)]">
          {cat.label}
          {rule && (
            <span className="tabular ml-1.5 rounded-full bg-[var(--surface-sunken)] px-1.5 text-[10px] uppercase tracking-[0.06em] text-[var(--text-subtle)]">
              {rule}
            </span>
          )}
        </span>
        <span className="tabular text-[11px] text-[var(--text-muted)]">{cat.weightPct}%</span>
        <span className="tabular w-16 text-right text-[11px] text-[var(--text-muted)]">
          {cat.entered.length === 0 ? '—' : `${cat.counted.length}${cat.droppedCount > 0 ? ` +${cat.droppedCount}` : ''}`}
        </span>
        <span className="tabular w-14 text-right text-[12.5px] text-[var(--text)]">
          {cat.average === null ? '—' : `${cat.average.toFixed(1)}%`}
        </span>
        <span aria-hidden className="tabular text-[11px] text-[var(--text-subtle)]">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="border-t border-[var(--rule)] px-2.5 py-2">
          {itemless ? (
            <CourseLevel courseId={courseId} cat={cat} />
          ) : (
            <ul className="divide-y divide-[var(--rule)]">
              {cat.items.map((item) => (
                <ItemLine key={item.itemId} item={item} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function ItemLine({ item }: { item: GradedItem }) {
  const [editing, setEditing] = useState(false)
  const setScore = useSetScore()

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] text-[var(--text)]">{item.title}</p>
        <span className="tabular text-[10px] text-[var(--text-subtle)]">
          {item.dueDisplay || 'no date'}
          {item.maxPoints !== null && ` · out of ${fmt(item.maxPoints)}`}
          {item.awaitingScore && <span className="ml-1.5 text-[var(--soon)]">awaiting</span>}
        </span>
      </div>

      {editing || item.pct === null ? (
        <ScoreBox
          current={item.pct}
          maxPoints={item.maxPoints}
          busy={setScore.isPending}
          autoFocus={editing}
          onSave={(body) => {
            setScore.mutate({ id: item.itemId, body, title: item.title })
            setEditing(false)
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`Change score for ${item.title}`}
          className="tap tabular rounded-[var(--radius)] border border-dashed border-[var(--border-strong)] px-2 text-[13px] text-[var(--text)]"
        >
          {fmt(item.pct)}%
        </button>
      )}
    </li>
  )
}

/** ME 334 attendance: one number for the whole category. */
function CourseLevel({ courseId, cat }: { courseId: string; cat: CategoryStanding }) {
  const set = useSetCategoryScore()
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="min-w-0 flex-1 text-[11.5px] leading-snug text-[var(--text-muted)]">
        No individual items — one figure for the whole category
        {cat.courseLevelPct !== null && (
          <span className="tabular ml-1.5 text-[var(--text)]">· {fmt(cat.courseLevelPct)}%</span>
        )}
      </p>
      <ScoreBox
        current={cat.courseLevelPct}
        maxPoints={null}
        busy={set.isPending}
        onSave={(body) => set.mutate({ courseId, key: cat.key, body })}
      />
    </div>
  )
}
